




































































































import moment from 'moment';
import { Component, Prop, Vue, Watch } from 'vue-property-decorator';
import Checkbox from '@/components/Form/Checkbox.vue';
import SelectField from '@/components/Form/SelectField.vue';
import AccordionList from '@/components/Global/Accordion/AccordionList.vue';
import Alert from '@/components/Global/Alert.vue';
import { DownloadButton } from '@/components/Global/Downloads/DownloadButton.vue';
import { DownloadIcon } from '@/components/Global/Downloads/DownloadIcon.vue';
import { DownloadProgress } from '@/components/Global/Downloads/DownloadProgress.vue';
import Loading from '@/components/Global/Loading.vue';
import { DocumentStatusMessageType } from '@/services/AsyncNotifications';
import {
	Download,
	DownloadEvent,
	DownloadListener,
	DownloadQueue,
	Ready,
	resolveDownloadUrl,
	UrlReadyWhen,
} from '@/services/Downloads';
import { PolicyPacket } from '@/services/PolicyDocuments';
import Reports from '@/services/Reports';
import { CredentialsReport, DownloadableCredentialRiskReport, Risk } from './ReportTypes';

@Component({
	components: {
		AccordionList,
		Alert,
		Checkbox,
		DownloadButton,
		DownloadIcon,
		DownloadProgress,
		Loading,
		SelectField,
	},
})
export default class MmicCredentialReportList extends Vue {
	@Prop() private report: CredentialsReport;
	@Prop() private reportType: string;

	private downloadAllId: string = '';
	private readonly paginate: string[] = ['reports'];
	private reports: DownloadableCredentialRiskReport[] = [];

	// The backend service that generates these reports is a bit slow. So to avoid overwhelming it,
	// only this number of reports are allowed to be requested at one time. Jim Vergin knows more
	// about this limitation.
	private static readonly _maxConcurrentReports: number = 4;

	private _previousReportUrls: Array<Promise<string>>;
	private _previousReportUrlsIndex: number;

	private created(): void {
		const generateDownloadId = (documentId: string): string =>
			`${this.$route.path}:${documentId}`;

		this.downloadAllId = generateDownloadId(this.report.policy_no);
		this.reports = this.report.risks.map(
			(risk: Risk): DownloadableCredentialRiskReport => ({
				risk,
				downloadId: generateDownloadId(risk.report_id),
			}),
		);
	}

	private async resolveReportUrl(report: DownloadableCredentialRiskReport): Promise<string> {
		const response = await Reports.requestCredentialReport(report.risk.report_id, 'credential');
		const urlReadyWhen: UrlReadyWhen = {
			ready: response.fetching ? Ready.later : Ready.now,
			url: response.s3_url,
		};
		return resolveDownloadUrl(
			urlReadyWhen,
			DocumentStatusMessageType.credentialReport,
			report.risk.report_id,
		);
	}

	private downloadReport(report: DownloadableCredentialRiskReport): Promise<Download> {
		// Fill the previous report URLs array with preresolved promises to make subsequent code
		// simpler. This happens here instead of using an initializer to save Vue from making these
		// fields reactive.
		if (!this._previousReportUrls) {
			this._previousReportUrls = new Array<Promise<string>>(
				MmicCredentialReportList._maxConcurrentReports,
			).fill(Promise.resolve(''), 0, MmicCredentialReportList._maxConcurrentReports);
			this._previousReportUrlsIndex = 0;
		}

		// Add report URL promises to the array in a round robin fashion such that one will start
		// only once the previous report in that slot completes.
		const previousReportUrl: Promise<string> = this._previousReportUrls[
			this._previousReportUrlsIndex
		];
		const currentReportUrl: Promise<string> = (async (): Promise<string> => {
			try {
				await previousReportUrl;
			} catch {
				// A failure in resolving the previous report should have no bearing on whether
				// this one starts.
			}
			return this.resolveReportUrl(report);
		})();
		this._previousReportUrls[this._previousReportUrlsIndex] = currentReportUrl;
		this._previousReportUrlsIndex =
			(this._previousReportUrlsIndex + 1) % this._previousReportUrls.length;

		return DownloadQueue.singleton.enqueue({
			fileNameFromClient: Reports.generateFileName({
				date: moment().format('YYYY-MM-DD'),
				extensionWithoutSeparator: 'pdf',
				policyNumber: this.report.policy_no,
				subtitle: report.risk.name,
				title: 'Credentialing Report',
			}),
			id: report.downloadId,
			label: `Credentialing Report for ${report.risk.name}`,
			urlPromise: currentReportUrl,
		});
	}

	private downloadAllReports(): Array<Promise<Download>> {
		return this.reports.map(
			(report: DownloadableCredentialRiskReport): Promise<Download> =>
				this.downloadReport(report),
		);
	}
}
